import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Loader2, Search, AlertTriangle, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_REFRESH_MS = 15 * 60 * 1000;

const issueBadgeStyles = {
  missing_canonical: "bg-yellow-100 text-yellow-800",
  missing_product_schema: "bg-red-100 text-red-800",
  duplicate_title: "bg-orange-100 text-orange-800",
};

export const AdminSEOAuditPage = () => {
  const [auditReport, setAuditReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  const fetchAudit = async (forceRefresh = false, silent = false) => {
    if (!silent) {
      setRefreshing(true);
      setError(null);
    }
    try {
      const response = await axios.get(`${API}/admin/seo/audit`, {
        params: {
          force_refresh: forceRefresh,
          origin: window.location.origin,
        },
      });
      setAuditReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch SEO audit report");
    }
    if (!silent) {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchAudit(false, true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredIssues = useMemo(() => {
    const issues = auditReport?.issues || [];
    const query = searchTerm.trim().toLowerCase();
    if (!query) return issues;
    return issues.filter((issue) => {
      const detailsText = Array.isArray(issue.details) ? issue.details.join(" ") : "";
      return `${issue.issue_type} ${issue.page_type} ${issue.url || ""} ${issue.title || ""} ${detailsText}`
        .toLowerCase()
        .includes(query);
    });
  }, [auditReport, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="seo-audit-loading-state">
        <Loader2 className="w-8 h-8 animate-spin text-warm-muted" />
      </div>
    );
  }

  return (
    <div data-testid="admin-seo-audit-page">
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl" data-testid="seo-audit-page-title">SEO Audit</h1>
          <p className="text-warm-muted" data-testid="seo-audit-page-subtitle">
            Automated SEO validation for home, shop, category, collection, and product pages.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Badge variant="outline" data-testid="seo-audit-auto-refresh-badge">Auto refresh: every 15 min</Badge>
          <Button
            onClick={() => fetchAudit(true)}
            disabled={refreshing}
            data-testid="seo-audit-refresh-button"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Run Audit Now
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3" data-testid="seo-audit-error-alert">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="seo-audit-summary-grid">
        <div className="bg-white rounded-lg shadow-warm p-4 border-l-4 border-blue-500">
          <p className="text-warm-muted text-sm">Pages Scanned</p>
          <p className="text-2xl font-semibold" data-testid="seo-audit-total-pages">{auditReport?.summary?.total_pages_scanned || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-warm p-4 border-l-4 border-red-500">
          <p className="text-warm-muted text-sm">Total Issues</p>
          <p className="text-2xl font-semibold" data-testid="seo-audit-total-issues">{auditReport?.summary?.total_issues || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-warm p-4 border-l-4 border-yellow-500">
          <p className="text-warm-muted text-sm">Missing Canonical</p>
          <p className="text-2xl font-semibold" data-testid="seo-audit-missing-canonical-count">{auditReport?.summary?.missing_canonical_count || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-warm p-4 border-l-4 border-orange-500">
          <p className="text-warm-muted text-sm">Duplicate Titles</p>
          <p className="text-2xl font-semibold" data-testid="seo-audit-duplicate-title-count">{auditReport?.summary?.duplicate_title_count || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-warm p-4 border-l-4 border-rose-500">
          <p className="text-warm-muted text-sm">Missing Product Schema</p>
          <p className="text-2xl font-semibold" data-testid="seo-audit-missing-schema-count">{auditReport?.summary?.missing_product_schema_count || 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-warm p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3 justify-between" data-testid="seo-audit-filter-bar">
        <div className="relative md:w-[380px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="pl-9"
            placeholder="Search by URL, issue type, title"
            data-testid="seo-audit-search-input"
          />
        </div>
        <div className="text-sm text-warm-muted" data-testid="seo-audit-generated-at-text">
          Generated: {auditReport?.generated_at ? new Date(auditReport.generated_at).toLocaleString() : "-"}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-warm overflow-hidden" data-testid="seo-audit-issues-table-card">
        <div className="p-4 border-b border-warm flex items-center justify-between">
          <h2 className="font-serif text-xl">Issue Table</h2>
          <Badge variant="secondary" data-testid="seo-audit-filtered-issues-count">{filteredIssues.length} issue rows</Badge>
        </div>

        {filteredIssues.length === 0 ? (
          <div className="p-10 text-center" data-testid="seo-audit-empty-state">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <p className="text-warm-dark font-medium">No SEO issues found for current filter.</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[620px]" data-testid="seo-audit-issues-table-wrapper">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  <th className="text-left p-3">Issue</th>
                  <th className="text-left p-3">Page Type</th>
                  <th className="text-left p-3">URL</th>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Missing / Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredIssues.map((issue, index) => (
                  <tr key={`${issue.issue_type}-${issue.url}-${index}`} className="border-t align-top" data-testid={`seo-audit-issue-row-${index}`}>
                    <td className="p-3">
                      <Badge className={issueBadgeStyles[issue.issue_type] || "bg-gray-100 text-gray-700"} data-testid={`seo-audit-issue-type-${index}`}>
                        {issue.issue_type}
                      </Badge>
                    </td>
                    <td className="p-3" data-testid={`seo-audit-issue-page-type-${index}`}>{issue.page_type}</td>
                    <td className="p-3 text-xs break-all text-blue-700" data-testid={`seo-audit-issue-url-${index}`}>{issue.url || "-"}</td>
                    <td className="p-3" data-testid={`seo-audit-issue-title-${index}`}>{issue.title || "-"}</td>
                    <td className="p-3" data-testid={`seo-audit-issue-details-${index}`}>
                      {Array.isArray(issue.details) && issue.details.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {issue.details.slice(0, 8).map((detail, detailIndex) => (
                            <Badge key={`${detail}-${detailIndex}`} variant="outline">{detail}</Badge>
                          ))}
                          {issue.details.length > 8 && <Badge variant="outline">+{issue.details.length - 8} more</Badge>}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-warm-muted flex items-center gap-2" data-testid="seo-audit-scope-note">
        <AlertTriangle className="w-4 h-4" />
        Coverage: {auditReport?.scan_scope || "home/shop/category/collection/product"}
      </div>
    </div>
  );
};
